import { useState } from 'react';
import { useCart } from '@/context/CartContext';

const colorOptions = [
  { id: 'crno-srebrno', name: 'Crno - srebrno', letterColor: 'silver', price: 0 },
  { id: 'crno-zlatno', name: 'Crno - zlatno', letterColor: 'gold', price: 0 },
];

const dimensionOptions = [
  { id: 'M', name: 'M', dims: '30x15', price: 0 },
  { id: 'L', name: 'L', dims: '40x20', price: 500 },
  { id: 'XL', name: 'XL', dims: '50x30', price: 1000 },
];

interface MiniConfiguratorProps {
  basePrice: number;
  baseProductName: string;
  productId: string;
  productImage: string;
  baseShapeName: string;
}

export default function MiniConfigurator({
  basePrice,
  baseProductName,
  productId,
  productImage,
  baseShapeName,
}: MiniConfiguratorProps) {
  const { addItem } = useCart();
  const [streetText, setStreetText] = useState('');
  const [numberText, setNumberText] = useState('');
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [selectedDimension, setSelectedDimension] = useState(dimensionOptions[0]);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const totalPrice = (basePrice + selectedColor.price + selectedDimension.price) * quantity;

  const getFinishColor = () => {
    return selectedColor.letterColor === 'gold' ? 'text-amber-500' : 'text-gray-300';
  };

  const getFinishShadow = () => {
    return selectedColor.letterColor === 'gold'
      ? '0 2px 8px rgba(245,158,11,0.4)'
      : '0 2px 8px rgba(156,163,175,0.4)';
  };

  const handleAddToCart = () => {
    const street = streetText.trim() || '';
    const number = numberText.trim() || '';
    const fullText = street && number ? `${street} ${number}` : street || number || 'Bez teksta';
    const itemName = `${baseProductName} - "${fullText}" (${selectedDimension.dims} cm, ${selectedColor.name})`;
    addItem({
      id: `${productId}-custom-${Date.now()}-${Math.floor(Math.random() * 999)}`,
      name: itemName,
      price: totalPrice,
      image: productImage,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const hasCustomization = streetText.trim().length > 0 || numberText.trim().length > 0 || selectedColor.id !== 'crno-srebrno' || selectedDimension.id !== 'M';

  const canAdd = streetText.trim().length > 0 || numberText.trim().length > 0;

  return (
    <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg">
          <i className="ri-settings-3-line" />
        </div>
        <h3 className="font-heading text-lg font-semibold text-foreground-950">
          Personalizuj svoj kućni broj
        </h3>
      </div>

      {/* Text inputs */}
      <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-label text-sm font-medium text-foreground-700 mb-2">
            Ulica / Prezime *
          </label>
          <input
            type="text"
            value={streetText}
            onChange={(e) => setStreetText(e.target.value)}
            placeholder="npr. Petrović, Jovanović..."
            maxLength={12}
            className="w-full px-4 py-3 border border-background-200 rounded-lg font-label text-base text-foreground-950 focus:border-amber-600 focus:outline-none focus:ring-1 focus:ring-amber-600/20"
          />
        </div>
        <div>
          <label className="block font-label text-sm font-medium text-foreground-700 mb-2">
            Broj *
          </label>
          <input
            type="text"
            value={numberText}
            onChange={(e) => setNumberText(e.target.value)}
            placeholder="npr. 48A, 15, 7B..."
            maxLength={8}
            className="w-full px-4 py-3 border border-background-200 rounded-lg font-label text-base text-foreground-950 focus:border-amber-600 focus:outline-none focus:ring-1 focus:ring-amber-600/20"
          />
        </div>
      </div>
      <p className="text-xs text-foreground-500 mb-5 -mt-2">
        Maksimalno 12 karaktera za ulicu / prezime, 8 za broj. Preporučujemo 5-8 za najbolji izgled.
      </p>

      {/* Color */}
      <div className="mb-5">
        <label className="block font-label text-sm font-medium text-foreground-700 mb-2">
          Boja
        </label>
        <div className="grid grid-cols-2 gap-2">
          {colorOptions.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedColor(c)}
              className={`p-3 rounded-lg border text-center transition-all ${
                selectedColor.id === c.id
                  ? 'border-amber-600 bg-amber-50'
                  : 'border-background-200 hover:border-background-300 bg-white'
              }`}
            >
              <div className="w-6 h-6 rounded-full mx-auto mb-1.5 border border-background-200 overflow-hidden flex">
                <span className="w-1/2 h-full bg-black" />
                <span className={`w-1/2 h-full ${c.letterColor === 'gold' ? 'bg-amber-400' : 'bg-gray-300'}`} />
              </div>
              <span className="font-label text-xs font-medium text-foreground-950">{c.name}</span>
              {c.price > 0 && (
                <span className="font-label text-xs text-foreground-500 block">+{c.price.toLocaleString()} RSD</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Dimensions */}
      <div className="mb-5">
        <label className="block font-label text-sm font-medium text-foreground-700 mb-2">
          Dimenzije
        </label>
        <div className="grid grid-cols-3 gap-2">
          {dimensionOptions.map((dim) => (
            <button
              key={dim.id}
              onClick={() => setSelectedDimension(dim)}
              className={`p-3 rounded-lg border text-center transition-all ${
                selectedDimension.id === dim.id
                  ? 'border-amber-600 bg-amber-50'
                  : 'border-background-200 hover:border-background-300 bg-white'
              }`}
            >
              <span className="block text-2xl font-bold text-foreground-950 mb-1">
                {dim.name}
              </span>
              <span className="font-label text-xs text-foreground-500">{dim.dims} cm</span>
              {dim.price > 0 && (
                <span className="font-label text-xs text-foreground-500 block">+{dim.price.toLocaleString()} RSD</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Live Preview */}
      <div className="mb-5">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-2 text-sm font-label font-medium text-amber-600 hover:text-amber-700 transition-colors mb-3"
        >
          <i className={showPreview ? 'ri-eye-off-line' : 'ri-eye-line'} />
          {showPreview ? 'Sakrij pregled' : 'Prikaži pregled'}
        </button>

        {showPreview && (
          <div className="relative rounded-lg overflow-hidden border border-background-200 bg-background-100">
            <div className="relative min-h-[200px] flex items-center justify-center p-6">
              {/* Dark backdrop */}
              <div className="absolute inset-0 bg-gradient-to-br from-background-200 to-background-300" />
              {/* Subtle texture overlay */}
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              {/* The sign plate */}
              <div
                className="relative z-10 px-8 py-5 rounded-lg border-2 shadow-lg bg-black/90 border-gray-700"
                style={{ minWidth: '140px' }}
              >
                <div className="absolute -inset-1 bg-black/15 rounded-lg blur-sm -z-10" />
                <span
                  className={`text-2xl font-bold ${getFinishColor()} block text-center leading-tight`}
                  style={{ textShadow: getFinishShadow() }}
                >
                  {streetText.trim() || numberText.trim() ? (
                    <>
                      {streetText.trim() && <span className="block">{streetText.trim()}</span>}
                      {numberText.trim() && <span className="block">{numberText.trim()}</span>}
                    </>
                  ) : (
                    '48A'
                  )}
                </span>
              </div>
            </div>
            {/* Info label */}
            <div className="absolute bottom-2 left-3 px-2 py-1 bg-black/50 rounded">
              <span className="font-label text-xs text-white">{baseShapeName} · {selectedDimension.dims} cm · {selectedColor.name}</span>
            </div>
          </div>
        )}
      </div>

      {/* Quantity & Price */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5 pt-4 border-t border-background-200">
        <div className="flex items-center gap-3">
          <span className="text-sm font-label text-foreground-700">Količina:</span>
          <div className="flex items-center border border-background-200 rounded-lg">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-9 h-9 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-subtract-line" />
            </button>
            <span className="w-9 text-center text-sm font-label text-foreground-950">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-9 h-9 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-add-line" />
            </button>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-label text-sm text-foreground-600">Ukupno:</span>
          <span className="font-label text-2xl font-bold text-foreground-950">
            {totalPrice.toLocaleString()} RSD
          </span>
          {hasCustomization && (
            <span className="font-label text-xs text-foreground-500">(personalizovano)</span>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleAddToCart}
          disabled={!canAdd}
          className={`flex-1 py-3.5 px-6 font-label text-sm font-medium rounded-full transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
            !canAdd
              ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
              : added
                ? 'bg-green-600 text-white'
                : 'bg-foreground-950 text-white hover:bg-foreground-900'
          }`}
        >
          <i className={added ? 'ri-check-line' : 'ri-shopping-cart-line'} />
          {added ? 'Dodato u korpu' : 'Dodaj personalizovani u korpu'}
        </button>
      </div>
    </div>
  );
}
